import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getParentAvatar } from '../constants/avatars';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import { subscribeToMessages, addMessage, migrateMessagesFromLocalStorage } from '../services/chatService';
import { API_ENDPOINTS } from '../config/api';

function Chat() {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [illustrations, setIllustrations] = useState({}); // Store illustrations locally (not in Firestore)
  const illustrationsRef = useRef({}); // Ref to track illustrations for subscription callback
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Load messages from Firestore and subscribe to real-time updates
  useEffect(() => {
    if (!currentUser?.uid) {
      return;
    }

    // Migrate messages from localStorage to Firestore (one-time migration)
    migrateMessagesFromLocalStorage(currentUser.uid).catch(error => {
      console.error('Error migrating messages:', error);
    });

    // Subscribe to real-time message updates
    const unsubscribe = subscribeToMessages(currentUser.uid, (firestoreMessages) => {
      // Merge Firestore messages with local illustrations from ref
      const illustrationMap = { ...illustrationsRef.current };
      
      // Apply illustrations to Firestore messages
      const messagesWithIllustrations = firestoreMessages.map(msg => ({
        ...msg,
        illustration: illustrationMap[msg.id] || null
      }));
      
      setMessages(messagesWithIllustrations);
    });

    return () => {
      unsubscribe();
    };
  }, [currentUser?.uid]);

  // Update illustrations ref when illustrations state changes
  useEffect(() => {
    illustrationsRef.current = illustrations;
  }, [illustrations]);

  // Update messages when illustrations change
  useEffect(() => {
    setMessages(prevMessages => 
      prevMessages.map(msg => ({
        ...msg,
        illustration: illustrations[msg.id] || msg.illustration || null
      }))
    );
  }, [illustrations]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleIllustrate = (messageId, imageUrl) => {
    // Store illustration locally (not in Firestore)
    setIllustrations(prev => ({
      ...prev,
      [messageId]: imageUrl
    }));
    // Update messages to include illustration
    setMessages(prev => prev.map(msg => 
      msg.id === messageId ? { ...msg, illustration: imageUrl } : msg
    ));
  };

  const handleSendMessage = async (messageText) => {
    if (!messageText.trim() || isLoading || !currentUser?.uid) return;

    // Add user message to Firestore
    const userMessage = {
      text: messageText,
      isBot: false,
      timestamp: new Date()
    };
    
    try {
      await addMessage(currentUser.uid, userMessage);
    } catch (error) {
      console.error('Error saving user message to Firestore:', error);
      // Continue even if Firestore save fails
    }

    setIsLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.CHAT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: messageText }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.details || `Server error: ${response.status}`);
      }

      const data = await response.json();

      // Add bot response to Firestore
      const botMessage = {
        text: data.response || data.error || "Sorry, I didn't understand that. Can you try asking again?",
        isBot: true,
        question: messageText,
        timestamp: new Date()
      };
      
      try {
        await addMessage(currentUser.uid, botMessage);
      } catch (error) {
        console.error('Error saving bot message to Firestore:', error);
        // Continue even if Firestore save fails
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        text: error.message || "Oops! Something went wrong. Please try again!",
        isBot: true,
        timestamp: new Date()
      };
      
      try {
        await addMessage(currentUser.uid, errorMessage);
      } catch (firestoreError) {
        console.error('Error saving error message to Firestore:', firestoreError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="chat-container">
      <div className="messages-container">
        {messages.map((message) => (
          <ChatMessage 
            key={message.id} 
            message={message} 
            messages={messages}
            onIllustrate={handleIllustrate}
          />
        ))}
        {isLoading && (
          <div className="message bot-message">
            <div className="avatar">
              <div className="avatar-circle">
                <img src={getParentAvatar(currentUser)} alt={currentUser?.displayName || 'Parent'} className="avatar-image" />
              </div>
            </div>
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <ChatInput onSendMessage={handleSendMessage} disabled={isLoading} />
    </div>
  );
}

export default Chat;

